import {
  Text,
  Image,
  Flex,
  Icon,
  Tag,
  VStack,
  HStack,
  Box,
} from '@everlywell/ui-kit';
import { Star } from '@phosphor-icons/react';
import Price from 'components/Price';
import ProductCardV2 from 'components/ProductCardV2';
import RichTextWrapper from 'components/RichTextWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import pluralize from 'pluralize';
import React from 'react';
import { notEmpty } from 'utils/types/utilityTypes';

export type ProductCardProps = {
  product: Queries.pdpContentfulProductFragment;
  cta: React.ReactNode;
  isCurrentProduct?: boolean;
};

/**
 * Product card that wraps the ProductCardV2 component used
 * in the CompareTestsSection component
 */
const ProductCard = ({
  product,
  cta,
  isCurrentProduct = false,
}: ProductCardProps) => {
  const price = product.spreeProduct?.variants?.find(
    (variant) => variant?.is_master,
  )?.price;

  const rating = product.spreeProduct?.powerReviews?.average_rating || 0;
  const numberOfReviews = product.spreeProduct?.powerReviews?.review_count || 0;
  const hideRating =
    product.metadata?.flags?.find(
      (flag) => flag?.metadata?.metadata_id === 'hideRating',
    )?.value === true;

  const tags =
    product.metadata?.flags
      ?.filter((flag) => flag?.metadata?.metadata_id === 'compare_tag')
      .map((flag) => flag?.displayText)
      .filter(notEmpty) || [];

  return (
    <ProductCardV2
      size="sm"
      children={
        <VStack spacing={3} alignItems="start" marginTop="4">
          {/* Product title */}
          <Text size="xl" fontWeight="medium">
            {product.spreeProduct?.name}
          </Text>
          {/* Product Rating */}
          {!hideRating && (
            <Flex gap={2} alignItems="center">
              <Icon
                as={Star}
                color="utility.rating"
                weight="fill"
                boxSize="20px"
              />
              <Text size="md">{`${rating} · ${pluralize(
                'Review',
                numberOfReviews,
                true,
              )}`}</Text>
            </Flex>
          )}

          {product.comparisonDescription && (
            <Box>
              <RichTextWrapper
                contentfulRichText={product.comparisonDescription}
                options={{
                  removeWrapper: true,
                }}
              />
            </Box>
          )}
        </VStack>
      }
      image={
        product.images?.[0]?.desktopImage?.gatsbyImageData && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bg: 'tints.lightCream',
                borderRadius: '50%',
                width: '120px',
                height: '120px',
              }}
            />
            <Box margin="auto">
              <Image
                as={GatsbyImage}
                image={product.images[0].desktopImage?.gatsbyImageData}
                alt={''}
                sx={{ maxW: '200px' }}
              />
            </Box>
          </>
        )
      }
      pricing={Number(price) && <Price price={Number(price)} />}
      colorScheme={isCurrentProduct ? 'green' : 'default'}
      tag={
        isCurrentProduct ? (
          <Tag variant="secondary" size="sm">
            This test
          </Tag>
        ) : (
          <HStack spacing={2}>
            {tags.map((tag, index: number) => (
              <Tag key={`tag-${index}`} size="sm">
                {tag}
              </Tag>
            ))}
          </HStack>
        )
      }
      cta={cta}
    />
  );
};

export default ProductCard;
